'use client';

import { useRouter } from 'next/navigation';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, Box, Button, IconButton, Paper, Typography } from '@mui/material';

import { clearLegs, setSelectMode, useAppDispatch, useAppSelector } from '@/store';

import { ROUTES } from '@/constants';

const MIN_LEGS = 2;

/** Floating bar shown while combo-select mode is active, mirroring a cart summary. */
export function ComboSelectionBar() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isSelectMode = useAppSelector((state) => state.comboSelection.isSelectMode);
  const legs = useAppSelector((state) => state.comboSelection.legs);

  if (!isSelectMode) return null;

  const handleExit = () => dispatch(setSelectMode(false));
  const handleClear = () => dispatch(clearLegs());
  const handleContinue = () => router.push(ROUTES.COMBOS);

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: (theme) => `0px 8px 24px ${alpha(theme.palette.common.black, 0.35)}`,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {legs.length === 0
          ? 'Tap outcomes to build a combo'
          : `${legs.length} leg${legs.length === 1 ? '' : 's'} selected`}
      </Typography>

      {legs.length > 0 && (
        <Button
          variant="text"
          size="small"
          onClick={handleClear}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Clear
        </Button>
      )}

      <Button
        variant="contained"
        size="small"
        disabled={legs.length < MIN_LEGS}
        onClick={handleContinue}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2 }}
      >
        Continue
      </Button>

      <IconButton size="small" onClick={handleExit} aria-label="Exit combo selection">
        <CloseIcon sx={{ fontSize: '1.1rem' }} />
      </IconButton>
    </Paper>
  );
}
